import React from 'react';
import { CircleNotch } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { cn } from '../../lib/utils';
import {
  loadSocialSignInConfig,
  SOCIAL_PROVIDER_LABEL,
  startSocialSignIn,
  type SocialProvider,
  type SocialSignInConfig,
  type SocialSignInIntent,
} from '../../lib/socialSignIn';

/**
 * Google's "G" mark. Google's brand rules require the four official colours on
 * a Sign in with Google button, so this third-party mark carries its own
 * values; nothing else here does (RUT-4523).
 */
function GoogleMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" aria-hidden className={className}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/** The stack's social sign-in settings; `undefined` while loading, `null` when off. */
export function useSocialSignInConfig(): SocialSignInConfig | null | undefined {
  const [config, setConfig] = React.useState<SocialSignInConfig | null | undefined>(undefined);
  React.useEffect(() => {
    let alive = true;
    loadSocialSignInConfig().then((c) => { if (alive) setConfig(c); });
    return () => { alive = false; };
  }, []);
  return config;
}

/**
 * Apple's logo. Apple's rules want it in one colour that matches the button's
 * text, so it takes `currentColor` and carries no colour of its own.
 */
function AppleMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 384 512" aria-hidden className={className} fill="currentColor">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

interface Props {
  intent: SocialSignInIntent;
  /**
   * Before leaving for the provider (e.g. remember attribution). Return false
   * to stay on the page (a required field is missing).
   */
  onStart?: () => boolean | void;
  /** Words between the buttons and the form below them. */
  dividerLabel?: string;
  /** Google button words; Google's rules allow "Continue with Google" / "Sign up with Google". */
  label?: string;
  /** Which providers this page offers; each still shows only when the stack has it. Default: all. */
  providers?: SocialProvider[];
  className?: string;
}

const ALL_PROVIDERS: SocialProvider[] = ['Google', 'SignInWithApple'];

/**
 * "Continue with Google" and "Continue with Apple", then a quiet "or" rule
 * above the email form. Each button renders only when the stack says that
 * provider is configured (`GET /health`), so a stack without them looks
 * exactly as it did.
 */
export function SocialSignInButtons({
  intent,
  onStart,
  dividerLabel = 'or use your email',
  label = 'Continue with Google',
  providers = ALL_PROVIDERS,
  className,
}: Props) {
  const config = useSocialSignInConfig();
  const [leaving, setLeaving] = React.useState<SocialProvider | null>(null);

  const offered = config ? providers.filter((provider) => config.providers.includes(provider)) : [];
  if (!config || offered.length === 0) return null;

  const start = async (provider: SocialProvider) => {
    if (onStart?.() === false) return;
    setLeaving(provider);
    try {
      await startSocialSignIn(config, provider, intent);
    } catch {
      setLeaving(null);
    }
  };

  return (
    <div className={cn('space-y-6', className)}>
      <div className="space-y-3">
        {offered.map((provider) => (
          <Button
            key={provider}
            type="button"
            variant="secondary"
            className="h-12 w-full text-base font-semibold"
            onClick={() => start(provider)}
            disabled={leaving !== null}
          >
            {leaving === provider ? (
              <CircleNotch className="h-5 w-5 animate-spin" />
            ) : provider === 'Google' ? (
              <GoogleMark className="!size-5" />
            ) : (
              <AppleMark className="!size-5" />
            )}
            {provider === 'Google' ? label : `Continue with ${SOCIAL_PROVIDER_LABEL[provider]}`}
          </Button>
        ))}
      </div>
      <div className="flex items-center gap-3" role="separator" aria-label={dividerLabel}>
        <Separator className="flex-1" />
        <span className="text-sm text-muted-foreground">{dividerLabel}</span>
        <Separator className="flex-1" />
      </div>
    </div>
  );
}
